import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * Loads linked Firestore account for the signed-in user (`users.accountId` → `accounts/{id}`).
 * Only fetches when the route is under `/portal/`.
 */
export function usePortalLinkedAccount() {
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const portalSide = pathname.startsWith('/portal/');
    if (!portalSide || !currentUser?.uid) {
      setAccount(null);
      setLoading(false);
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const accountId = userDoc.exists() ? userDoc.data()?.accountId : null;
        if (!accountId) {
          if (!cancelled) setAccount(null);
          return;
        }
        const accountDoc = await getDoc(doc(db, 'accounts', accountId));
        if (!cancelled && accountDoc.exists()) {
          setAccount({ id: accountDoc.id, ...accountDoc.data() });
        } else if (!cancelled) {
          setAccount(null);
        }
      } catch (e) {
        console.error('usePortalLinkedAccount:', e);
        if (!cancelled) setAccount(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pathname, currentUser?.uid]);

  return { account, loading };
}
